import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, forkJoin, map, Observable, of, switchMap, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private _notifications: BehaviorSubject<Notification[]> = new BehaviorSubject<Notification[]>([]);
  private eventSource: EventSource | null = null;
  private token: string = localStorage.getItem('yl_accessToken');

  /**
   * Constructor
   */
  constructor(
    private _httpClient: HttpClient,
    private _zone: NgZone,
  ) { }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for notifications
   */
  get notifications$(): Observable<Notification[]> {
    return this._notifications.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all notifications
   */
  getAll(userId: string = null): Observable<Notification[]> {
    if (!userId) {
      return of([]);
    }
    const legacyUrl = `${environment.apiUrl}/notifications/${userId}/notifications?page=1&limit=10`;
    const newUrl = `${environment.workloadApiUrl}/communication/notifications`;

    return forkJoin([
      this._httpClient.get<Notification[]>(legacyUrl).pipe(
        map(legacyNotifications =>
          legacyNotifications.map(notification => ({ ...notification, isLegacy: true }))
        )
      ),
      this._httpClient.get<{ count: number, rows: Notification[] }>(newUrl),
    ]).pipe(
      map(([legacyNotifications, newNotifications]) => {
        const allNotifications = [...legacyNotifications, ...newNotifications.rows];
        return allNotifications.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      }),
      tap(notifications => this._notifications.next(notifications))
    );
  }

  /**
   * Create a notification
   *
   * @param notification
   */
  create(notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient
          .post<Notification>('api/common/notifications', { notification })
          .pipe(
            map((newNotification) => {
              // Update the notifications with the new notification
              this._notifications.next([...notifications, newNotification]);

              // Return the new notification from observable
              return newNotification;
            }),
          ),
      ),
    );
  }

  /**
   * Update the notification
   *
   * @param id
   * @param notification
   */
  update(id: string, notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient
          .patch<Notification>('api/common/notifications', {
            id,
            notification,
          })
          .pipe(
            map((updatedNotification: Notification) => {
              // Find the index of the updated notification
              const index = notifications.findIndex((item) => item.id === id);

              // Update the notification
              notifications[index] = updatedNotification;

              // Update the notifications
              this._notifications.next(notifications);

              // Return the updated notification
              return updatedNotification;
            }),
          ),
      ),
    );
  }

  markAsRead(notification: Notification) {
    // If notification is of type Workload send the message to workload
    if (notification.isLegacy) {
      this._httpClient.patch<boolean>(`${environment.workloadApiUrl}/notifications/${notification.id}/read`, {}).subscribe(result => {
        if (result) {
          notification.read = true;
          this._notifications.next(this._notifications.value);
        }
      })
    } else {
      const updateDto = {
        read: true,
      };
      this._httpClient.put<boolean>(`${environment.workloadApiUrl}/communication/notifications/${notification.id}`, updateDto).subscribe();
      return;
    }
  }

  addNotifications(addedNotification: Notification): Observable<Notification[]> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) => {
        let updatedNotifications: Notification[] = [...notifications, addedNotification];
        this._notifications.next(updatedNotifications);
        return of(updatedNotifications);
      }
      ));
  }

  /**
   * Delete the notification
   *
   * @param id
   */
  delete(deltedNotification: Notification): void {
    if (deltedNotification.isLegacy) {
      const id = deltedNotification.id;
      this._httpClient.delete<boolean>(`${environment.apiUrl}/notifications/${id}/delete`, {}).subscribe((result) => {
        if (result) {
          this.notifications$.pipe(take(1)).subscribe((notifications) => {
            const updatedNotifications = notifications.filter(notification => notification.id !== deltedNotification.id);
            this._notifications.next(updatedNotifications);
          });
        }
      });
    } else {
      this._httpClient.delete<boolean>(`${environment.workloadApiUrl}/communication/notifications/${deltedNotification.id}`)
        .subscribe((result) => {
          if (result) {
            this.notifications$.pipe(take(1)).subscribe((notifications) => {
              const updatedNotifications = notifications.filter(notification => notification.id !== deltedNotification.id);
              this._notifications.next(updatedNotifications);
            });
          }
        });
    }
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient
          .get<boolean>('api/common/notifications/mark-all-as-read')
          .pipe(
            map((isUpdated: boolean) => {
              // Go through all notifications and set them as read
              notifications.forEach((notification, index) => {
                notifications[index].read = true;
              });

              // Update the notifications
              this._notifications.next(notifications);

              // Return the updated status
              return isUpdated;
            }),
          ),
      ),
    );
  }

  markAllNotificationAsSeen(notifications: Notification[]) {
    if (!notifications || notifications.length === 0) {
      return;
    }

    const unSeenNewNotifications = notifications.filter(noti => !noti.seen && !noti.isLegacy);
    const unReadLegasyNotifications = notifications.filter(noti => !noti.read && noti.isLegacy);

    if (unSeenNewNotifications.length > 0) {
      const ids = unSeenNewNotifications.map(noti => noti.id);
      this._httpClient.put<boolean>(`${environment.workloadApiUrl}/communication/notifications/markAllAsSeen`, { ids }).subscribe();
    }
    if (unReadLegasyNotifications.length > 0) {
      const ids = unReadLegasyNotifications.map(noti => noti.id);
      this._httpClient.put<boolean>(`${environment.apiUrl}/notifications/markAllAsSeen`, { ids }).subscribe();
    }
  }

  public cleanup() {
    if (this.eventSource) {
      this.eventSource.close();
    }
  }
}
